import {
  LitElement,
  classMap,
  createRef,
  css,
  html,
  nothing,
  ref,
  styleMap,
} from '@lit'

import * as paths from '@nova/paths'
import styles from '@nova/styles'
import { formatMoney, handleize } from '@nova/utils'

import { addToCart, pushToGTMDataLayer } from '@nova/utils'

const quickShopState = {
  initial: 'SHOW_OPTION_SELECTOR',
  start: 'SHOW_OPTION_STARTED',
  doing: 'SHOW_OPTION_DOING',
  submit: 'SHOW_ADD_TO_CART',
  loading: 'SHOW_LOADING',
  confirm: 'SHOW_CONFIRMATION',
  error: 'ERROR',
}

class NovaProductCard extends LitElement {
  static properties = {
    url: {
      type: String,
    },
    available: {
      state: true,
    },
    productAvailable: {
      type: String,
      attribute: 'product-available',
    },
    title: {
      type: String,
    },
    subtitle: {
      type: String,
    },
    image: {
      type: String,
      attribute: 'image',
    },
    imageAlt: {
      type: String,
    },
    secondaryImage: {
      type: String,
      attribute: 'secondary-image',
    },
    secondaryImageAlt: {
      type: String,
    },
    compareAtPrice: {
      type: String,
      attribute: 'compare-at-price',
    },
    swatches: {
      type: Array,
    },
    sizes: {
      type: Array,
    },
    price: {
      type: String,
    },
    vendor: {
      type: String,
    },
    callout: {
      type: String,
    },
    variants: {
      type: Array,
    },
    badge: {
      type: Object,
    },
    badgeImage: {
      type: String,
      attribute: 'badge-image',
    },
    activeIndex: {
      type: Number,
    },
    options: {
      type: Array,
    },
    hideRatings: {
      attribute: 'hide-ratings',
    },
    showWishlist: {
      type: Boolean,
      attribute: 'show-wishlist',
      converter: (value) => Boolean(value),
    },
    hideSwatches: {
      state: true,
    },
    isHovered: {
      state: true,
    },
    isMobile: {
      state: true,
    },
    slidesForCarousel: {
      type: Number,
      state: true,
    },
    quickShopVariant: {
      type: Object,
      state: true,
    },
    status: {
      type: String,
      state: true,
    },
    productOptions: {
      type: Array,
    },
    isLowStock: {
      type: Boolean,
      attribute: 'is-low-stock',
      reflect: true,
    },
    enableHoverImage: {
      type: String,
      attribute: 'show-hover-image',
    },
    loading: {
      type: String,
      default: 'lazy',
    },
    fetchPriority: {
      type: String,
      attribute: 'fetch-priority',
      default: 'auto'
    }
  }

  $productCardContentRef = createRef()

  get activeId() {
    return this.swatchVariants[this.activeIndex]?.id || this.id
  }

  get activePrice() {
    if (this.quickShopVariant) return formatMoney(this.quickShopVariant.price)

    if (!this.swatchVariants.length) return formatMoney(this.price)

    return formatMoney(this.swatchVariants[this.activeIndex]?.price)
  }

  get activeAvailable() {
    if (this.quickShopVariant) return this.quickShopVariant.available

    if (this.swatchVariants[this.activeIndex]) {
      return this.swatchVariants[this.activeIndex]?.available
    }
    return this.available
  }

  get activeImage() {
    let image =
      this.swatchVariants[this.activeIndex]?.featured_image?.src ||
      this.image ||
      ''

    const isSmall = image
      .split('_')
      .some(
        (fragment) => fragment.includes('x1150') || fragment.includes('1150x') || fragment.includes('width=1150'),
      )

    if (!isSmall) {
      const fragments = image.split('.')
      fragments[fragments.length - 2] = `${
        fragments[fragments.length - 2]
      }_1150x`
      image = fragments.join('.')
      image = image.includes('?') ? `${image}&width=1150` : `${image}?width=1150`
    }

    return image
  }

  get activeUrl() {
    const variantId = this.quickShopVariant
      ? this.quickShopVariant.id
      : this.swatchVariants[this.activeIndex]?.id
    return variantId ? `${this.url}?variant=${variantId}` : this.url
  }

  get allVariantsBySwatch() {
    const swatch = this.swatchVariants[this.activeIndex]?.swatchTitle

    return this.variants.filter((variant) => variant.options?.includes(swatch))
  }

  get activeCompareAtPrice() {
    if (this.variants?.length > 1) {
      const activeVariant =
        this.quickShopVariant || this.swatchVariants[this.activeIndex]
      if (activeVariant) {
        const _activeCompareAtPrice = Number.parseFloat(
          activeVariant?.compare_at_price,
        )
        const _activePrice = Number.parseFloat(activeVariant?.price)

        if (!_activeCompareAtPrice) return false

        if (_activeCompareAtPrice > _activePrice) {
          return formatMoney(
            this.swatchVariants[this.activeIndex]?.compare_at_price,
          )
        }
      } else {
        if (Number(this.compareAtPrice) > Number(this.price)) {
          return formatMoney(
            this.compareAtPrice,
          )
        }
      }
    } else {
      if (!this.compareAtPrice) return false

      if (Number(this.compareAtPrice) > Number(this.price))
        return formatMoney(this.compareAtPrice)
    }

    return false
  }

  constructor() {
    super()

    this.available = true
    this.activeIndex = 0
    this.variants = []
    this.swatches = []
    this.options = []
    this.swatchVariants = []
    this.swatchesPosition = 0
    this.swatchesPerSlide = 5
    this.activeSwatchPage = 0
    this.slidesForCarousel = 1
    this.isHovered = false
    this.isMobile = false
    this.status = quickShopState.initial
    this.isLowStock = false
    this.hideRatings = window.Shopify.shop.includes('wholesale') // Hide ratings by default on wholesale store
    this.enableHoverImage = 'true'
  }

  connectedCallback() {
    super.connectedCallback()

    this.available = this.productAvailable === 'true'
  }

  async firstUpdated() {
    if (this.options?.length) {
      this.setActiveByVariantId(this.id, true)
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('activeIndex')) {
      this.requestUpdate()
    }

    if (changedProperties.has('activeImage')) {
      this.requestUpdate()
    }
  }

  handleOptionSelection(event) {
    this.status = quickShopState.doing
  }

  handleOptionStarted(event) {
    this.status = quickShopState.start
  }

  handleAddToWishlist(e) {
    const { variantId } = e.target.dataset

    if (!variantId) return

    alert(variantId)
  }

  handleMouseEnter() {
    this.isHovered = true
  }

  handleFocus() {
    // Show desktop quickShop on focus
    if (window.innerWidth >= 1200) {
      this.isHovered = true
    }
  }

  handleMouseLeave() {
    this.isHovered = false
    this.status = quickShopState.initial
  }

  handleMediaClick(event) {
    if (event.target.closest('button, [role="button"], nova-product-card-quick-shop, sl-button, .product-card__wishlist, .swym-button')) {
      return
    }

    // Trigger click on product title
    const titleElement = this.querySelector('.js-product-card-title')
    if (titleElement) {
      titleElement.click()
    }
  }

  setActiveIndex(index) {
    this.activeIndex = index
  }

  setActiveByVariantId(id, setPage = false) {
    const variantIndex = this.swatchVariants.findIndex(
      (variant) => variant.id === Number.parseInt(id),
    )

    if (variantIndex > -1) {
      this.setActiveIndex(variantIndex)
    }

    if (setPage) {
      const pageNumber = this.paginatedSwatches.findIndex((page) =>
        page.some((variant) => variant.id === Number.parseInt(id)),
      )
      if (pageNumber > -1 && pageNumber !== this.activeSwatchPage) {
        this.setSwatchPage(pageNumber)
      }
    }
  }

  closeQuickShop() {
    this.status = quickShopState.initial
  }

  initialQuickShop() {
    this.status = quickShopState.initial
    this.isHovered = false
  }

  sanitizeString(string) {
    return string
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
  }

  async handleAddToCartClick(e) {
    this.status = quickShopState.loading
    try {
      const variantId = e.detail.variant.id || e.target.dataset.variantId
      await addToCart({ id: variantId })
      this.status = quickShopState.confirm
    } catch (error) {
      this.status = quickShopState.error
    } finally {
      const quickShop = this.shadowRoot.querySelector(
        'nova-product-card-quick-shop',
      )
      quickShop.setActiveVariant(null)
      setTimeout(() => {
        this.status = quickShopState.initial
      }, 5000)
    }
  }

  renderMedia() {
    return html`
      <div
        part="media"
        class="product-card__media ${classMap({
          'product-card__media--enable-hover': this.secondaryImage && this.enableHoverImage === 'true',
        })}"
        @click=${this.handleMediaClick}>
        ${
          this.image
            ? html`
            <div class="product-card__image product-card__image--primary">
              <img src=${this.activeImage} alt=${this.title} loading="${this.loading}" fetchpriority=${this.fetchPriority} />
            </div>`
            : nothing
        }

        ${
          this.secondaryImage && this.enableHoverImage === 'true'
            ? html`
            <div class="product-card__image product-card__image--secondary">
              <img src=${this.secondaryImage} alt=${this.title} loading="lazy" />
            </div>`
            : nothing
        }

        ${this.renderBadges()}

        <div class='hidden block--xl'>
          <nova-product-card-quick-shop
            url=${this.url}
            product-available=${this.available}
            status=${this.status}
            class=${classMap({ 'visually-hidden': !this.isHovered })}
						product-options=${JSON.stringify(this.productOptions)}
            variants=${JSON.stringify(this.variants)}
            @keydown=${this.handleQuickShopKeyDown}
            @click=${this.handleQuickShopClick}
          >
          </nova-product-card-quick-shop>
        </div>
      </div>
    `
  }

  renderBadges() {
    let badgeConfig = null;

    // NOTE: Determine badge display priority and ordering
    // if (this.compareAtPrice && this.compareAtPrice > this.price) {
    //   badgeConfig = {
    //     text: __NOVA_GLOBAL__.saleBadgeText,
    //     bgColor: __NOVA_GLOBAL__.saleBadgeBg,
    //     color: __NOVA_GLOBAL__.saleBadgeColor,
    //     borderColor: __NOVA_GLOBAL__.saleBadgeBorder
    //   };
    // } else if (this.isLowStock) {
    if (this.isLowStock) {
      badgeConfig = {
        text: __NOVA_GLOBAL__.inventoryBadgeText,
        bgColor: __NOVA_GLOBAL__.inventoryBadgeBg,
        color: __NOVA_GLOBAL__.inventoryBadgeColor,
        borderColor: __NOVA_GLOBAL__.inventoryBadgeBorder
      };
    } else if (this.badge) {
      badgeConfig = {
        text: this.badge.title,
        image: this.badgeImage || '',
        imageAltText: this.badge.image_alt_text || '',
        bgColor: this.badge.background || '',
        color: this.badge.text_color || '',
        borderColor: this.badge.text_color || ''
      };
    }

    if (!badgeConfig) return nothing;

    const styleVars = {
      '--badge-bg-color': badgeConfig.bgColor,
      '--badge-color': badgeConfig.color,
      '--badge-border-color': badgeConfig.borderColor
    };

    return html`
      <div class="badges">
        ${
          badgeConfig.image
            ? html`
                <span class="badges__item badges__item--image">
                  <img
                    src=${badgeConfig.image}
                    alt=${badgeConfig.imageAltText || ''}
                    loading="lazy"
                  />
                </span>
              `
            : html`
                <span class="badges__item" style=${styleMap(styleVars)}>
                  ${badgeConfig.text}
                </span>
              `
        }
      </div>
    `;
  }

  renderQuickShopCTAs() {
    if (this.status === quickShopState.initial) return null

    if (
      this.status === quickShopState.submit ||
      this.status === quickShopState.loading
    ) {
      return html`
        <sl-button
          variant="primary:ink"
          class="product-card__atc"
          ?disabled=${this.quickShopVariant.available !== true}
          data-variant-id=${this.quickShopVariant.id}
          ?loading=${this.status === quickShopState.loading}
          @click=${this.handleAddToCartClick}>
          ${this.quickShopVariant.available ? 'Add to Bag' : 'Sold Out'}
        </sl-button>
      `
    }

    if (this.status === quickShopState.confirm) {
      return html`
        <div class="product-card__atc product-card__atc--confirm">
          <span>Item Added to Bag!</span>
        </div>
      `
    }

    if (this.status === quickShopState.error) {
      return html`
        <div class="product-card__atc product-card__atc--confirm product-card__atc--error">
          <span>Something Went Wrong</span>
        </div>
      `
    }

    return null
  }

  render() {
    return html`
      <sl-card
        slot="card"
        id="product-card-${this.id}"
        class="product-card"
        style="--padding: 0; --border-width: 0; --sl-shadow-x-small: 0;"
        tabindex="0"
        @focus=${this.handleFocus}
        @mouseenter=${this.handleMouseEnter}
        @mouseleave=${this.handleMouseLeave}
        @option-selected=${this.handleOptionSelection}
        @option-started=${this.handleOptionStarted}
        @add-to-bag=${this.handleAddToCartClick}
        @close-quick-shop=${this.closeQuickShop}
        @initial-quick-shop=${this.initialQuickShop}>
        ${this.renderMedia()}
        <div class="product-card__content" ${ref(this.$productCardContentRef)}>
          <div class="product-card__body">
            <header class="product-card__header">
              <slot name="reviews"></slot>
              ${
                this.vendor
                  ? html`<span class="product-card__vendor">${this.vendor}</span>`
                  : nothing
              }
              <h3 class="product-card__title h6">
                <slot name="title-link"></slot>
              </h3>
              ${
                this.callout
                  ? html`<span class="product-card__callout">${this.callout}</span>`
                  : nothing
              }
            </header>
            <nova-product-card-price
              class="product-card__price"
              .price=${this.activePrice}
              .compareAtPrice=${this.activeCompareAtPrice}
              data-price=${this.activePrice}
              data-compare-at-price=${this.activeCompareAtPrice}>
            </nova-product-card-price>
          </div>
          <div class="product-card__quick-shop--md">
            <nova-product-card-quick-shop
              product-options=${JSON.stringify(this.productOptions)}
              variants=${JSON.stringify(this.variants)}
              url=${this.url}
              product-available=${this.available}
              status=${this.status}
              @keydown=${this.handleQuickShopKeyDown}
              @click=${this.handleQuickShopClick}
            >
            </nova-product-card-quick-shop>
          </div>
					<div class="product-card__quick-shop--sm ${classMap({
            'product-card__quick-shop--sm-active':
              this.status === quickShopState.start ||
              this.status === quickShopState.doing,
          })}">
            <nova-product-card-quick-shop
              product-options=${JSON.stringify(this.productOptions)}
              variants=${JSON.stringify(this.variants)}
              url=${this.url}
              product-available=${this.available}
              status=${this.status}
              @keydown=${this.handleQuickShopKeyDown}
              @click=${this.handleQuickShopClick}
            >
            </nova-product-card-quick-shop>
          </div>
        </div>
      </sl-card>
      <slot name="wishlist-button"></slot>
    `
  }

  static styles = [
    styles.reset,
    styles.a11y,
    styles.flex,
    styles.helpers,
    styles.typography,
    styles.skeleton,
    css`
      :host {
        display: flex;
        flex-direction: column;
        position: relative;
        container-type: inline-size;
        width: 100%;
        height: 100%;
        position: relative;
      }

      @media screen and (min-width: 1200px) {
        .hidden--xl {
          display: none !important;
        }

        .block--xl {
          display: block !important;
        }

        .flex--xl {
          display: flex !important;
        }
      }

      .product-card {
        height: 100%;
      }
      .product-card::part(base) {
        background: transparent;
        height: 100%;
      }

      .product-card::part(body) {
        display: flex;
        flex-direction: column;
        height: 100%;
      }

      .product-card__content {
        padding-top: var(--nova-space-sm);
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        flex: 1;
      }

      .product-card__body {
        display: flex;
        flex-direction: column;
        gap: var(--nova-space-sm);
      }

      @media screen and (min-width: 768px) {
        .product-card__body {
          gap: var(--nova-space-xs);
        }
      }

      .product-card__header {
        display: flex;
        align-items: start;
        gap: var(--nova-space-xs);
        flex-direction: column;
      }

      .product-card__media {
        position: relative;
        overflow: hidden;
        border-radius: 8px;
        background: var(--nova-color-brand-reverse);
        box-shadow: 0px 3px 10px 0px rgba(0, 0, 0, 0.10);
        cursor: pointer;
      }

      @media screen and (min-width: 1200px) {
        .product-card__media {
          border-radius: 12px;
        }
      }

      .product-card .badges {
        position: absolute;
        top: var(--nova-space-sm);
        left: var(--nova-space-sm);
      }

      nova-product-card-quick-shop {
        display: block;
        position: absolute;
        bottom: var(--nova-space-sm);
        left: var(--nova-space-sm);
        right: var(--nova-space-sm);
      }

      .product-card:hover nova-product-card-quick-shop {
        display: block;
      }

      .product-card__image {
        width: 100%;
        aspect-ratio: 1;
        background: linear-gradient(150.38deg, rgb(222, 222, 218) 3.74%, rgba(222, 222, 218, 0.49) 63.73%, rgba(222, 222, 218, 0) 85.34%);
        transition: opacity 0.3s ease-in-out;
      }

      .product-card__image img {
        display: block;
        width: 100%;
        height: auto;
        aspect-ratio: 1;
        object-fit: contain;
        object-position: center;
        background: var(--nova-color-brand-reverse);
      }

      .product-card__image--primary {
        opacity: 1;
      }

      .product-card__image--secondary {
        position: absolute;
        top: 0;
        left: 0;
        opacity: 0;
      }

      .product-card__media--enable-hover:hover .product-card__image--primary {
        opacity: 0;
        position: absolute;
        top: 0;
        left: 0;
      }

      .product-card__media--enable-hover:hover .product-card__image--secondary {
        opacity: 1;
        position: relative;
      }

      .product-card__vendor {
        font: var(--nova-body-caption);
        line-height: calc(10 / 12 * 100%);
      }

      .product-card__callout {
        font: var(--nova-pullquote);
        line-height: calc(20 / 16 * 100%);
      }

      .product-card__price {
        font: var(--nova-body-2-font);
        line-height: 100%;
      }

      @media screen and (min-width: 1024px) {
        .product-card__price {
          line-height: calc(16 / 18 * 100%);
        }
      }

      .product-card__price dd {
        margin: 0;
      }

      .product-card__info {
        overflow-y: hidden;
      }

      .product-card__info::-webkit-scrollbar {
        display: none;
      }

      @container (min-width: 300px) {
        .product-card__info { padding: 0 }
      }

      .product-card__title {
      	margin: 0;
      }

      .product-card__quick-shop--sm,
			.product-card__quick-shop--md {
        display: none;
      }
      @media screen and (max-width: 1199px) {
        .product-card__quick-shop--md {
          display: block;
          margin-top: var(--nova-space-sm);
        }

        .product-card__quick-shop--md nova-product-card-quick-shop {
          position: unset;
        }

        .product-card__quick-shop--md
        nova-product-card-quick-shop::part(option-list) {
          position: absolute;
          margin: 0;
          width: 100%;
          left: 0;
          bottom: 0;
          border-bottom-left-radius: 8px;
          border-bottom-right-radius: 8px;
        }
      }

      @media screen and (max-width: 767px) {
        .product-card__quick-shop--md
        nova-product-card-quick-shop::part(option-list),
				.product-card__quick-shop--md
        nova-product-card-quick-shop::part(overlay) {
          display: none;
        }

				.product-card__quick-shop--sm-active {
					display: block;
					position: fixed;
					z-index: 10;
					inset: 0;
				}
				.product-card__quick-shop--sm-active
				nova-product-card-quick-shop {
					inset: 0;
				}
				.product-card__quick-shop--sm-active
				nova-product-card-quick-shop::part(base) {
					display: none;
				}
				.product-card__quick-shop--sm-active
				nova-product-card-quick-shop::part(overlay) {
					border-radius: 0;
				}
				.product-card__quick-shop--sm-active
				nova-product-card-quick-shop::part(option-list) {
					bottom: 0;
    			position: fixed;
    			width: 100%;
					margin: 0;
				}
      }

      .product-card__wishlist {
        position: absolute;
        right: var(--nova-space-sm);
        top: var(--nova-space-sm);
      }

      .product-card__wishlist svg {
        pointer-events: none;
      }

      .product-card__wishlist:hover svg {
        fill: var(--nova-brand-secondary);
      }

      .product-card__wishlist:active svg {
        fill: var(--nova-color-text);
      }

      .badges {
        display: flex;
        gap: var(--nova-space-2xs);
      }

      .badges__item {
        align-items: flex-start;
        border-radius: var(--nova-border-rounded);
        background: var(--badge-bg-color, transparent);
        color: var(--badge-color, var(--nova-color-text));
        border: 1px solid var(--badge-border-color, var(--nova-color-text));
        display: flex;
        font: 700 10px/120% var(--nova-font-sans-bold);
        padding: var(--nova-space-2xs, 10px) 15px;
        text-transform: uppercase;
        pointer-events: none;
      }

      .badges__item--image {
        background: transparent;
        margin: 0;
        padding: 0;
        border: 0;
      }

      .badges__item--image img {
        display: block;
        width: 60px;
        height: auto;
      }

      sl-button[variant='primary:ink'] {
        width: 100%;
      }

      sl-button[variant='primary:ink']::part(base) {
        transition: 0.15s;
      }
      sl-button[variant='primary:ink']::part(base):hover {
        background-color: transparent;
      }
      sl-button[variant='primary:ink'] {
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
      }

      sl-button[variant='primary:ink']::part(base) {
        background-color: var(--nova-brand-ink);
        border: none;
        color: white;
      }

      sl-button[variant='primary:ink']::part(base):hover {
        color: white;
        background-color: var(--nova-brand-primary);
      }

      sl-button[disabled]::part(base):hover {
        background-color: var(--nova-brand-neutral);
      }

      .product-card__atc {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
      }

      .product-card__atc span {
        line-height: 40px;
      }

      .product-card__atc--confirm {
        width: 100%;
        height: 40px;
        display: flex;
        justify-content: center;
        background-color: white;
        border-radius: var(--nova-button-radius);
        background-color: var(--nova-brand-secondary);
      }

      .product-card__atc--error {
        background-color: var(--nova-utility-error);
      }
    `,
  ]
}

customElements.define('nova-product-card', NovaProductCard)
